import { useEffect, useState } from "react";
import { Copy, Eye, EyeOff, Loader2, RefreshCw, ShieldAlert } from "lucide-react";
import { toast } from "sonner";
import { getFoundationARTriggers } from "@/lib/api";

const copy = (text, msg) => { navigator.clipboard?.writeText(text); toast.success(msg); };

// Read-only view of the triggers on dbo.ar_invoice / dbo.ar_invoice_gl.
// Bodies are NULL until `GRANT VIEW DEFINITION` is run for the bridge
// login on the Practice database — the panel shows that exact GRANT.
export default function ARTriggerInspector() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [openTrig, setOpenTrig] = useState(null);

  const load = () => {
    setLoading(true);
    getFoundationARTriggers().then((r) => setData(r.data))
      .catch((e) => toast.error(e?.response?.data?.detail || "Could not read Foundation triggers"))
      .finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  const events = (t) => ["on_insert", "on_update", "on_delete"].filter((k) => t[k]).map((k) => k.slice(3)).join("/") || "—";
  const granted = data?.view_definition;

  return (
    <div className="mt-3 rounded-md border border-slate-200 p-2" data-testid="fs-ar-triggers">
      <div className="flex items-start justify-between gap-2 flex-wrap">
        <div>
          <div className="text-[11px] font-semibold text-slate-700 uppercase tracking-wide">AR triggers on Foundation (read-only)</div>
          <div className="text-[11px] text-slate-500">Triggers on <code>ar_invoice</code> / <code>ar_invoice_gl</code>{data?.database ? ` in ${data.database} as ${data.login}` : ""}. One of the insert triggers is the suspect behind FK <code>{data?.fk || "fk_ar_inv_gl_ar_inv"}</code>.</div>
        </div>
        <button type="button" onClick={load} disabled={loading} className="text-[11px] text-blue-600 hover:underline inline-flex items-center gap-1 disabled:opacity-50" data-testid="fs-ar-triggers-refresh">
          {loading ? <Loader2 className="w-3 h-3 animate-spin" /> : <RefreshCw className="w-3 h-3" />} Refresh
        </button>
      </div>

      {data?.error && (
        <div className={`mt-1 text-[11px] rounded px-2 py-1 border ${data.bridge_too_old ? "text-amber-900 bg-amber-50 border-amber-200" : "text-rose-700 bg-rose-50 border-rose-200"}`} data-testid="fs-ar-triggers-error">
          {data.error}{data.bridge_too_old ? " The inspector reads through the bridge's fixed diag queries only — no free-form SQL." : ""}
        </div>
      )}

      {data && !data.error && (
        <div className={`mt-1.5 rounded-md border p-1.5 text-[11px] ${granted ? "border-emerald-200 bg-emerald-50 text-emerald-900" : "border-amber-300 bg-amber-50 text-amber-900"}`} data-testid="fs-ar-triggers-grant">
          <div className="flex items-center gap-1 font-semibold">{granted ? <Eye className="w-3 h-3" /> : <EyeOff className="w-3 h-3" />}{granted ? "VIEW DEFINITION granted — trigger bodies are readable below." : "Trigger bodies hidden — the bridge login has no VIEW DEFINITION."}</div>
          {!granted && (
            <div className="mt-1 flex items-start gap-2">
              <pre className="flex-1 font-mono text-[10px] bg-white border border-amber-200 rounded px-1.5 py-1 whitespace-pre-wrap" data-testid="fs-ar-triggers-grant-sql">{data.grant_sql}</pre>
              <button type="button" onClick={() => copy(data.grant_sql, "GRANT statement copied")} className="h-6 px-1.5 text-[10px] rounded border border-amber-300 bg-white hover:bg-amber-100 inline-flex items-center gap-1" data-testid="fs-ar-triggers-copy-grant"><Copy className="w-3 h-3" /> Copy</button>
            </div>
          )}
          {!granted && <div className="mt-0.5 text-[10px]">Read-only, Practice database only. Run it in SSMS, then Refresh — the bodies appear immediately, no bridge change needed.</div>}
        </div>
      )}

      {data && data.triggers?.length > 0 && (
        <div className="mt-1.5 rounded border border-slate-200 text-[11px]" data-testid="fs-ar-triggers-list">
          {data.triggers.map((t) => (
            <div key={t.name} className="border-b border-slate-100 last:border-b-0">
              <div className="px-2 py-1 flex items-center gap-2 flex-wrap">
                <span className="font-mono text-slate-800">{t.parent_table}.<strong>{t.name}</strong></span>
                <span className="text-slate-500">{events(t)}{t.instead_of ? " · INSTEAD OF" : ""}{t.first_insert ? " · first" : ""}{t.last_insert ? " · last" : ""}</span>
                {t.is_disabled && <span className="text-[10px] px-1 rounded bg-slate-200 text-slate-600">disabled</span>}
                {t.on_insert && t.parent_table === "ar_invoice" && <span className="inline-flex items-center gap-0.5 text-[10px] px-1 rounded bg-rose-50 text-rose-700 border border-rose-200"><ShieldAlert className="w-3 h-3" /> suspect</span>}
                {t.readable ? (
                  <button type="button" onClick={() => setOpenTrig(openTrig === t.name ? null : t.name)} className="ml-auto text-[10px] text-blue-600 hover:underline" data-testid={`fs-ar-trigger-toggle-${t.name}`}>{openTrig === t.name ? "hide body" : "show body"}</button>
                ) : <span className="ml-auto text-[10px] text-slate-400">body hidden</span>}
                {t.readable && <button type="button" onClick={() => copy(t.definition, `${t.name} copied`)} className="text-[10px] text-blue-600 hover:underline" data-testid={`fs-ar-trigger-copy-${t.name}`}>copy</button>}
              </div>
              {data.dependencies?.[t.name] && <div className="px-2 pb-1 text-[10px] text-slate-500">references: {data.dependencies[t.name].join(", ")}</div>}
              {openTrig === t.name && t.readable && (
                <pre className="mx-2 mb-1.5 max-h-72 overflow-auto whitespace-pre-wrap font-mono text-[10px] text-slate-800 bg-slate-50 border border-slate-200 rounded px-1.5 py-1" data-testid={`fs-ar-trigger-body-${t.name}`}>{t.definition}</pre>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
